import { distinctByLiteralProperty } from '@fmnts/core';

interface BaseTemplate {
  /** Type of the expression */
  type: 'literal' | 'expression';
  /** Raw input that was parsed */
  raw: string;
  /** Start of the expression */
  start: number;
  /** End of the expression (non inclusive) */
  end: number;
}

/**
 * A literal from a templated string.
 * It reprensents any part of a templated string that
 * is a literal.
 *
 * @example
 * const parts = splitTemplate('some {{field}}');
 * parts[0].type === 'literal';
 * parts[0].raw === 'some ';
 */
export interface TemplateLiteral extends BaseTemplate {
  type: 'literal';
}

/**
 * An expression from a templated string.
 *
 * It represents instances of any part in
 * a templated string in the form of
 * `{{ some-expression }}`.
 */
export interface TemplateExpression extends BaseTemplate {
  type: 'expression';
  /**
   * Expression to be evaluted.
   *
   * @example
   * // for a templated string `{{ field }}`
   * t.expression === 'field' // true
   * */
  expression: string;
}

/**
 * A part of a templated string.
 */
export type TemplatePart = TemplateLiteral | TemplateExpression;

const distinctTemplatePart = distinctByLiteralProperty<TemplatePart>()('type');

/**
 * Type guard for `TemplateLiteral`
 *
 * @param o Template part
 * @returns
 * `true` if `o` is `TemplateLiteral`
 */
export const isTemplateLiteral =
  distinctTemplatePart<TemplateLiteral>('literal');

/**
 * Type guard for `TemplateExpression`
 *
 * @param o Template part
 * @returns
 * `true` if `o` is `TemplateExpression`
 */
export const isTemplateExpression =
  distinctTemplatePart<TemplateExpression>('expression');

/**
 * Error that is thrown if a template part could not be evaluated.
 */
export class TemplateEvaluationError extends Error {
  public override readonly name = 'TemplateEvaluationError';

  constructor(
    /** The evaluated template part */
    public readonly part: TemplatePart,
    message: string,
  ) {
    super(message);
  }
}
